import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Trash2, FileText, Loader2, Calendar, X } from "lucide-react";
import { useApp } from "../context/AppContext";
import { generateWorksheet } from "../lib/api";
import { Markdown } from "../lib/markdown";
import { WorksheetView } from "../components/WorksheetView";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

export default function Library() {
  const { sessions, removeSession } = useApp();
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [worksheet, setWorksheet] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const allTags = useMemo(() => {
    const s = new Set();
    sessions.forEach((x) => (x.tags || []).forEach((t) => s.add(t)));
    return Array.from(s);
  }, [sessions]);

  const filtered = useMemo(() => {
    return sessions.filter((s) => {
      const q = query.toLowerCase();
      const matchesQ = !q || s.question.toLowerCase().includes(q) || (s.synthesis || "").toLowerCase().includes(q);
      const matchesTag = !activeTag || (s.tags || []).includes(activeTag);
      return matchesQ && matchesTag;
    });
  }, [sessions, query, activeTag]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 5) { toast.error("You can select up to 5 sessions"); return prev; }
      return [...prev, id];
    });
  };

  const generate = async () => {
    if (selected.length < 2) { toast.error("Select at least 2 sessions"); return; }
    setGenLoading(true);
    try {
      const chosen = sessions.filter((s) => selected.includes(s.id))
        .map((s) => ({ question: s.question, synthesis: s.synthesis }));
      const libQ = sessions.filter((s) => !selected.includes(s.id)).map((s) => s.question);
      const data = await generateWorksheet(chosen, libQ);
      setWorksheet(data);
      setSheetOpen(true);
    } catch (e) {
      toast.error("Could not generate the worksheet. Try again.");
    } finally {
      setGenLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tighter" style={{ color: "#1E293B" }}>Your Library</h1>
          <p className="mt-1" style={{ color: "#475569" }}>
            {sessions.length} saved {sessions.length === 1 ? "session" : "sessions"}. Select 2–5 to generate an Insight Worksheet.
          </p>
        </div>
        <Link to="/ask" data-testid="new-session-btn"
          className="h-11 px-5 rounded-xl text-sm font-bold text-white grid place-items-center transition-colors duration-200"
          style={{ background: "#2563EB" }}>+ New Session</Link>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 h-11 px-4 rounded-xl border bg-white" style={{ borderColor: "#CBD5E1" }}>
          <Search size={17} color="#94A3B8" />
          <input data-testid="library-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search questions and answers…" className="w-full outline-none text-sm" style={{ color: "#1E293B" }} />
        </div>
      </div>

      {allTags.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          <TagChip active={!activeTag} onClick={() => setActiveTag(null)} label="All" />
          {allTags.map((t) => (
            <TagChip key={t} active={activeTag === t} onClick={() => setActiveTag(activeTag === t ? null : t)} label={t} />
          ))}
        </div>
      )}

      {sessions.length === 0 ? (
        <div className="mt-16 text-center">
          <div className="grid place-items-center w-16 h-16 rounded-2xl mx-auto mb-5" style={{ background: "#EFF6FF" }}>
            <FileText size={28} color="#2563EB" />
          </div>
          <h3 className="text-xl font-bold" style={{ color: "#1E293B" }}>No sessions yet</h3>
          <p className="mt-2" style={{ color: "#475569" }}>Ask the council your first question to start building your library.</p>
          <Link to="/ask" className="inline-block mt-5 h-11 px-6 rounded-xl text-sm font-bold text-white leading-[44px]" style={{ background: "#2563EB" }}>
            Ask a question
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((s) => {
            const isSel = selected.includes(s.id);
            return (
              <div key={s.id} data-testid="session-card"
                className="rounded-2xl border bg-white p-5 shadow-sm flex flex-col transition-transform duration-200 hover:-translate-y-1"
                style={{ borderColor: isSel ? "#2563EB" : "#CBD5E1", borderWidth: isSel ? 2 : 1 }}>
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={isSel} onChange={() => toggleSelect(s.id)}
                    data-testid="session-select"
                    className="mt-1 w-5 h-5 accent-[#2563EB] cursor-pointer shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-bold leading-snug line-clamp-3" style={{ color: "#1E293B" }}>{s.question}</p>
                    <p className="flex items-center gap-1.5 text-xs mt-2" style={{ color: "#94A3B8" }}>
                      <Calendar size={12} /> {new Date(s.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                  </div>
                </div>
                <p className="text-sm mt-3 line-clamp-3 leading-relaxed" style={{ color: "#475569" }}>
                  {(s.synthesis || "").replace(/[#*`]/g, "").slice(0, 160)}…
                </p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {(s.tags || []).map((t) => (
                    <span key={t} className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#ECFDF5", color: "#047857" }}>{t}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-4 pt-4 border-t" style={{ borderColor: "#F1F5F9" }}>
                  <button onClick={() => setExpanded(s)} data-testid="view-session-btn"
                    className="text-sm font-semibold transition-colors duration-200" style={{ color: "#2563EB" }}>Read full</button>
                  <button onClick={() => removeSession(s.id)} data-testid="delete-session-btn"
                    className="p-2 rounded-lg transition-colors duration-200" style={{ color: "#94A3B8" }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 bg-white border shadow-lg rounded-2xl px-5 py-3"
          style={{ borderColor: "#CBD5E1" }} data-testid="worksheet-bar">
          <span className="text-sm font-semibold" style={{ color: "#1E293B" }}>{selected.length} selected</span>
          <button onClick={generate} disabled={genLoading || selected.length < 2} data-testid="generate-worksheet-btn"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-xl text-sm font-bold text-white transition-colors duration-200 disabled:opacity-50"
            style={{ background: "#2563EB" }}>
            {genLoading ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
            Generate Worksheet
          </button>
          <button onClick={() => setSelected([])} className="p-1.5 rounded-lg" style={{ color: "#94A3B8" }}><X size={18} /></button>
        </div>
      )}

      {/* Full session dialog */}
      <Dialog open={!!expanded} onOpenChange={(o) => !o && setExpanded(null)}>
        <DialogContent className="max-w-2xl rounded-2xl border max-h-[85vh] overflow-y-auto" style={{ borderColor: "#CBD5E1" }}>
          {expanded && (
            <div>
              <DialogTitle className="sr-only">Session detail</DialogTitle>
              <h2 className="text-xl font-black tracking-tight pr-6" style={{ color: "#1E293B" }}>{expanded.question}</h2>
              <div className="mt-4"><Markdown text={expanded.synthesis} /></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Worksheet dialog */}
      <Dialog open={sheetOpen} onOpenChange={setSheetOpen}>
        <DialogContent className="max-w-5xl rounded-2xl border max-h-[88vh] overflow-y-auto" style={{ borderColor: "#CBD5E1", background: "#F8FAFC" }}>
          <div>
            <DialogTitle className="sr-only">Insight Worksheet</DialogTitle>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-4" style={{ color: "#2563EB" }}>Insight Worksheet</p>
            <WorksheetView data={worksheet} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TagChip({ active, onClick, label }) {
  return (
    <button onClick={onClick} data-testid={`tag-${label.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
      className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors duration-200"
      style={{ borderColor: active ? "#2563EB" : "#CBD5E1", background: active ? "#2563EB" : "#fff", color: active ? "#fff" : "#475569" }}>
      {label}
    </button>
  );
}
